import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import {
  esES,
  enUS,
  DataGrid,
  GridColumnVisibilityModel,
  DataGridProps,
  GridSelectionModel,
  GridRowsProp
} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import {
  FrecuentlyFailureResponse,
  IReasonByFilterResponse,
  ServiceType
} from 'api/catalog';
import { Priority } from 'api/client';

import DataGridPagination from '../DataGridPagination';
import { CustomToolbar } from './CustomToolbar';
import DataGridNoRowsOverlay from '../DataGridNoRowsOverlay';
import { TOption, TypeButtons } from '../ExportOptions';

type Ids = {
  id?: number;
  jobCategoryId?: number;
  kitId?: number;
  productId?: number;
  createdDate?: number;
  stageId?: number;
  warehouseId?: number;
  ticketId?: number;
  supplyStockId?: number;
  responseTimeId?: number;
  shipmentId?: number;
};

type Props<T> = Partial<DataGridProps> & {
  rows: Array<
    | FrecuentlyFailureResponse
    | ServiceType
    | IReasonByFilterResponse
    | T
    | GridRowsProp[]
  >;
  filename?: string;
  rowsPerPageOptions?: number[];
  onSelectionModelChange?: (data?: { [key: string]: unknown }[]) => void;
  style?: CSSProperties;
  getRowId?: (row: T) => string | number;
  messageNoRowsOverlay?: string;
  itemsToExport?: { [key: string]: unknown }[] | T[];
  headerToExport?: { label: string; key: string }[];
  activeClass?: boolean;
  onExport?: (selectedOption?: TOption) => void;
  typeFormatExport?: (type: string) => string;
  isExport?: boolean;
  isSendingEmail?: boolean;
  isLandscapePDF?: boolean;
  sendEmailBase64?: boolean;
  filterButton?: TypeButtons;
  handleUploadFile?: (data: {
    fileContent: string;
    fileName: string;
    description: string;
  }) => void;
  idToExport?: string;
  fixExcelSelection?: boolean;
  idName?: string;
  exportFormatter?: (data: unknown) => { [key: string]: unknown };
  handleSelectForExcelDownload?: (ids: GridSelectionModel) => void;
  handleSelectItemForExport?: (ids: GridSelectionModel) => void;
  itemsSelectedForExcelExportV2?: T[];
  itemsSelectedForExport?: T[];
  maximRowToExport?: number;
  paginationMode?: 'server' | 'client';
  selectionModel?: number[];
  setActiveCookie?: (data: unknown) => void;
};

export interface Density {
  value: string;
  headerHeight: number;
  rowHeight: number;
  factor: number;
}

type ItemRow = {
  enabled?: boolean;
  priorities?: Priority[];
  createdDateTime?: string;
  createdDate?: string;
  closedDate?: string;
  accessDate?: string;
  dateTime?: string;
  lastAccessDate?: string;
  isClientProperty?: boolean;
};

const joinPrioritiesClient = (prioritiesList: Priority[]) =>
  prioritiesList?.map((priority) => priority?.name).join(', ');

export const formatDateUTCLocal = (date: string | Date) => {
  if (date) {
    if (String(date)?.includes('T'))
      return dayjs.utc(date).local().format('DD-MM-YYYY HH:mm:ss');
    return date;
  }
  return '-';
};

const setIsClientProperty = (isClientProperty: boolean | string) => {
  if (typeof isClientProperty === 'boolean') {
    return isClientProperty ? 'Si' : 'No';
  }
  return isClientProperty;
};

function CatalogDataGrid<T>({
  activeClass = false,
  checkboxSelection = true,
  columns,
  columnVisibilityModel: columnVisibilityModelProps,
  setActiveCookie,
  exportFormatter = null,
  filename,
  filterButton,
  fixExcelSelection,
  getRowId,
  handleSelectForExcelDownload,
  handleUploadFile,
  headerToExport,
  idName = 'id',
  idToExport,
  isExport = true,
  isLandscapePDF,
  isSendingEmail = false,
  itemsSelectedForExcelExportV2,
  itemsToExport,
  loading,
  messageNoRowsOverlay,
  onExport,
  typeFormatExport,
  onSelectionModelChange,
  paginationMode = 'server',
  rows,
  rowsPerPageOptions,
  sendEmailBase64,
  selectionModel,
  style,
  handleSelectItemForExport,
  itemsSelectedForExport,
  ...props
}: Props<T>) {
  const { t, i18n } = useTranslation('global');

  const [densityState, setDensity] = useState<Density>();
  const [selectedItems, setSelectedItems] = useState<
    { [key: string]: unknown }[]
  >([]);

  const [selectedItemsForExcel, setSelectedItemsForExcel] = useState<
    { [key: string]: unknown }[]
  >([]);

  const itemsSelectedForExcelExport = useRef<{ [key: string]: unknown }[]>([]);

  useEffect(() => {
    if (fixExcelSelection) return;

    setSelectedItems((prev) =>
      prev.map((item: Ids) => {
        // TODO: Inspect types
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const itemToReplace: ItemRow = rows?.find(
          ({
            id,
            kitId,
            jobCategoryId,
            productId,
            stageId,
            createdDate,
            warehouseId,
            ticketId,
            supplyStockId,
            responseTimeId,
            shipmentId
          }: Ids) =>
            (id ||
              jobCategoryId ||
              productId ||
              kitId ||
              stageId ||
              createdDate ||
              warehouseId ||
              ticketId ||
              supplyStockId ||
              responseTimeId ||
              shipmentId) ===
            (item?.id ||
              item?.productId ||
              item.kitId ||
              item?.jobCategoryId ||
              item?.stageId ||
              item?.createdDate ||
              item?.warehouseId ||
              item?.ticketId ||
              item?.supplyStockId ||
              item?.responseTimeId ||
              item?.shipmentId)
        );

        if (itemToReplace) {
          return {
            ...itemToReplace,
            enabled: setStatus(itemToReplace?.enabled),
            priorities: joinPrioritiesClient(itemToReplace?.priorities),
            createdDateTime: formatDateUTCLocal(itemToReplace?.createdDateTime),
            createdDate: formatDateUTCLocal(itemToReplace?.createdDate),
            dateTime: formatDateUTCLocal(itemToReplace?.dateTime),
            accessDate: formatDateUTCLocal(itemToReplace?.accessDate),
            lastAccessDate: formatDateUTCLocal(itemToReplace?.lastAccessDate),
            isClientProperty: setIsClientProperty(
              itemToReplace?.isClientProperty
            )
          };
        }
        return item;
      })
    );
  }, [rows, fixExcelSelection]);

  const setStatus = (status: boolean) =>
    status
      ? t('common.commonProperties.active')
      : t('common.commonProperties.inactive');

  const handleSelection = (ids: GridSelectionModel): void => {
    let filteredItems: { [key: string]: unknown }[] = selectedItems
      // Quita los items que ya estan en el array
      .filter((item: Ids) =>
        ids.includes(
          item?.id ||
            item?.jobCategoryId ||
            item?.productId ||
            item?.kitId ||
            item?.stageId ||
            item?.createdDate ||
            item?.warehouseId ||
            item?.ticketId ||
            item?.supplyStockId ||
            item?.responseTimeId ||
            item?.shipmentId
        )
      )
      // Agrega los items que no estan en el array
      .concat(
        rows?.filter((item: Ids) =>
          ids.includes(
            item?.id ||
              item?.jobCategoryId ||
              item?.productId ||
              item.kitId ||
              item?.stageId ||
              item?.createdDate ||
              item?.warehouseId ||
              item?.ticketId ||
              item?.supplyStockId ||
              item?.responseTimeId ||
              item?.shipmentId
          )
        )
      )
      // Quita los duplicados
      .filter(
        (item, index, self) =>
          index ===
          self.findIndex(
            (t: Ids) =>
              (t?.id ||
                t?.jobCategoryId ||
                t?.productId ||
                t?.kitId ||
                t?.stageId ||
                t?.createdDate ||
                t?.warehouseId ||
                t?.supplyStockId ||
                t?.ticketId ||
                t?.responseTimeId ||
                t?.shipmentId) ===
              (item?.id ||
                item?.jobCategoryId ||
                item?.productId ||
                item.kitId ||
                item?.stageId ||
                item?.createdDate ||
                item?.warehouseId ||
                item?.ticketId ||
                item?.supplyStockId ||
                item?.responseTimeId ||
                item?.shipmentId)
          )
      )
      // Setea el estatus con valor activo o inactivo
      .map((item: ItemRow) => ({
        ...item,
        enabled:
          typeof item?.enabled === 'boolean'
            ? setStatus(item?.enabled)
            : item?.enabled,
        priorities:
          item?.priorities instanceof Array
            ? joinPrioritiesClient(item.priorities)
            : item?.priorities,
        createdDateTime: formatDateUTCLocal(item?.createdDateTime),
        createdDate: formatDateUTCLocal(item?.createdDate),
        dateTime: formatDateUTCLocal(item?.dateTime),
        closedDate: formatDateUTCLocal(item?.closedDate),
        accessDate: formatDateUTCLocal(item?.accessDate),
        lastAccessDate: formatDateUTCLocal(item?.lastAccessDate),
        isClientProperty: setIsClientProperty(item?.isClientProperty)
      }));

    if (exportFormatter) {
      filteredItems = filteredItems.map(exportFormatter);
    }

    if (onSelectionModelChange) onSelectionModelChange(filteredItems || []);
    setSelectedItems(filteredItems || []);
  };

  useEffect(() => {
    if (exportFormatter) {
      const itemsFormated = (
        itemsSelectedForExport || itemsSelectedForExcelExportV2
      ).map(exportFormatter);
      return setSelectedItemsForExcel(itemsFormated);
    }
    return setSelectedItemsForExcel(
      (itemsSelectedForExport || itemsSelectedForExcelExportV2)?.map(
        (item: object & { enabled?: boolean | string }) => ({
          ...item,
          enabled: item?.enabled
            ? t('common.commonProperties.active')
            : t('common.commonProperties.inactive')
        })
      )
    );
  }, [
    exportFormatter,
    itemsSelectedForExcelExportV2,
    itemsSelectedForExport,
    t
  ]);

  const handleSelectionV2 = useCallback(
    (ids: GridSelectionModel) => {
      const wasItemSelected =
        itemsSelectedForExcelExport.current.length > ids.length;

      const currentId = wasItemSelected
        ? itemsSelectedForExcelExport.current.find(
            (item) => !ids.includes(item[idName])
          )[idName]
        : ids[ids.length - 1];

      if (wasItemSelected) {
        const filteredItems = itemsSelectedForExcelExport.current.filter(
          (item) => item[idName] !== currentId
        );

        itemsSelectedForExcelExport.current = filteredItems;
        return;
      }

      const item = rows.find((row) => row[idName] === currentId);

      itemsSelectedForExcelExport.current = [
        ...itemsSelectedForExcelExport.current,
        {
          ...item,
          ...(item?.enabled ? { enabled: setStatus(item?.enabled) } : {})
        }
      ];
    },
    [rows, idName]
  );

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      ...columnVisibilityModelProps
    });

  const handleStateChange = useCallback(
    ({ density }: { density: Density }) => {
      if (density.value !== densityState?.value) {
        setDensity(density);
      }
    },
    [densityState?.value]
  );

  // traduzco itemsToExport en caso de que haya un campo "enabled"
  const itemsToExportTranslated = useMemo(
    () =>
      itemsToExport?.map((item) => {
        if (
          (item || {})?.hasOwnProperty('enabled') &&
          typeof item.enabled === 'boolean'
        ) {
          return {
            ...item,
            enabled: setStatus(item.enabled)
          };
        }
        return item;
      }),
    [itemsToExport]
  );

  return (
    <div
      className={activeClass ? 'tableModal' : props.className || ''}
      style={{
        ...style,
        display: 'flex',
        height: '100%',
        width: '100%'
      }}
    >
      <div style={{ flexGrow: 1 }}>
        <DataGrid
          {...props}
          checkboxSelection={checkboxSelection}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          components={{
            Toolbar: CustomToolbar,
            LoadingOverlay: LinearProgress,
            Pagination: DataGridPagination,
            NoRowsOverlay: DataGridNoRowsOverlay,
            NoResultsOverlay: DataGridNoRowsOverlay
          }}
          componentsProps={{
            noRowsOverlay: {
              customMessageNoRows:
                messageNoRowsOverlay ||
                t('common.commonMessages.noResultsFound')
            },
            noResultsOverlay: {
              customMessageNoRows:
                messageNoRowsOverlay ||
                t('common.commonMessages.noResultsFound')
            },
            toolbar: {
              isExport,
              fileName: filename?.substring(0, 30),
              onExport,
              typeFormatExport,
              limitCSVandXLSX: true,
              filterButton,
              rowCount:
                selectedItemsForExcel?.length ||
                selectedItems?.length ||
                props?.rowCount,
              itemsToExport: itemsToExportTranslated,
              headerToExport,
              itemsSelectedToExport:
                selectedItemsForExcel ||
                (fixExcelSelection
                  ? itemsSelectedForExcelExport.current
                  : selectedItems),
              isLandscapePDF,
              isSendingEmail,
              sendEmailBase64,
              handleUploadFile,
              maximRowToExport: props?.maximRowToExport,
              printOptions: { hideFooter: true, hideToolbar: true }
            }
          }}
          density="comfortable"
          getEstimatedRowHeight={() => 70}
          getRowHeight={() => 'auto'}
          getRowId={(row: {
            id?: number;
            dateTime?: string;
            clientId?: number;
            ticketId?: number;
          }) => {
            if (getRowId) return getRowId(row);
            return row?.id || row?.dateTime || row?.clientId || row?.ticketId;
          }}
          hideFooter={false}
          loading={loading}
          localeText={{
            ...(i18n.language === 'es'
              ? esES.components.MuiDataGrid.defaultProps.localeText
              : enUS.components.MuiDataGrid.defaultProps.localeText),
            toolbarDensityCompact: t('common.dataGrid.densityCompact'),
            toolbarDensityStandard: t('common.dataGrid.densityStandard'),
            toolbarDensityComfortable: t('common.dataGrid.densityComfortable')
          }}
          onColumnVisibilityModelChange={(newModel) => {
            setColumnVisibilityModel(newModel);
            setActiveCookie(newModel);
          }}
          onSelectionModelChange={
            handleSelectItemForExport ||
            handleSelectForExcelDownload ||
            (fixExcelSelection ? handleSelectionV2 : handleSelection)
          }
          onStateChange={handleStateChange}
          paginationMode={paginationMode}
          rows={rows || []}
          rowsPerPageOptions={rowsPerPageOptions || [25, 50, 75, 100]}
          selectionModel={
            selectionModel ||
            (itemsSelectedForExport || itemsSelectedForExcelExportV2)?.map(
              (item) => item[idName]
            )
          }
          sx={{
            '.MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'center'
            },
            textAlign: 'center',
            '.MuiDataGrid-columnHeaders': {
              backgroundColor: '#0B2E6A',
              minHeight: '39px',
              lineHeight: 'normal !important',
              color: 'white',
              '.MuiSvgIcon-root': {
                fill: 'white'
              },
              '.MuiIconButton-root': {
                padding: '0px 0px 0px 5px'
              }
            },
            '.MuiLinearProgress-root': {
              height: '8px'
            },
            '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
              outline: 'none'
            },
            '.MuiDataGrid-main': {
              minHeight: '300px'
            },
            '& .MuiDataGrid-row': {
              borderTop: 1
            },
            '.MuiStack-root': {
              '@media (max-width: 750px)': {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                width: '100%',
                marginTop: '.5em'
              }
            },
            ...(densityState?.value !== 'comfortable' && {
              '#actionsTable': {
                maxHeight: densityState?.rowHeight,
                '& .MuiButtonBase-root': {
                  width: densityState?.value === 'compact' ? '30px' : '32px',
                  height: densityState?.value === 'compact' ? '30px' : '32px',
                  margin: '0px 4px',
                  '& .MuiSvgIcon-root': {
                    width: '0.9em',
                    height: '0.9em'
                  }
                }
              }
            }),
            '.MuiDataGrid-columnHeaderTitle': {
              display: 'block',
              whiteSpace: 'break-spaces',
              overflow: 'unset',
              textOverflow: 'unset',
              position: 'relative'
            },
            '.MuiDataGrid-cell': {
              padding:
                densityState?.value === 'compact'
                  ? '0px 0px 0px 0px'
                  : densityState?.value === 'standard'
                  ? '5px 0px 5px 0px'
                  : '10px 0px 10px 0px',
              wordBreak: 'break-word',
              textAlign: 'center',
              justifyContent: 'center'
            },
            ...props?.sx
          }}
          disableColumnMenu
          disableSelectionOnClick
          keepNonExistentRowsSelected
          pagination
        />
      </div>
    </div>
  );
}

export default CatalogDataGrid;
